import React from "react";

import { faBox, faPlay, faWarning } from "@fortawesome/pro-regular-svg-icons";

import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { ColumnType } from "src/featureCatalogue/Queries/Create/helpers";
import { SQLPreviewTable } from "src/featureCatalogue/Queries/SQLResonsePreviewTable";
import {
  FEATURE_QUERY_PREVIEW_LIMIT,
  UseFeatureQueryPreview,
} from "src/featureCatalogue/queries";
import { errorMessage } from "src/utils/stringUtils";

export const SQLResponsePreview: React.FC<{
  featurePreview: UseFeatureQueryPreview;
  columnTypes: Record<string, ColumnType>;
  onColumnTypeChange: (column: string, type: ColumnType) => void;
  isEditMode: boolean;
}> = ({ featurePreview, columnTypes, onColumnTypeChange, isEditMode }) => {
  const isServerError = featurePreview.error?.response?.status === 500;
  const errorTitle = isServerError
    ? "We have received an unexpected error"
    : "Syntax error";

  const errorDetail = (() => {
    if (!isServerError) {
      return "No columns were returned. Please check the Feature Query.";
    }
    return featurePreview.error?.response?.data.detail
      ? errorMessage(featurePreview.error.response.data.detail)
      : "Our team has been notified";
  })();

  const { previewData } = featurePreview;
  const schemaColumns = previewData?.columns ?? [];
  const rowCount = previewData?.data?.length ?? 0;
  const hasNoRows = rowCount === 0;
  const isLimited = rowCount >= FEATURE_QUERY_PREVIEW_LIMIT;

  if (
    !featurePreview.error &&
    previewData &&
    hasNoRows &&
    schemaColumns.length === 0
  ) {
    return (
      <EmptyState
        description="To preview results, edit the Query or adjust the End date and time so the time window includes sample data."
        headline="No data exists for this Feature Query"
        icon={faBox}
      />
    );
  }

  return (
    <div className="decideScrollbar flex h-full flex-col overflow-hidden">
      {featurePreview.isIdle && (
        <div className="flex flex-1 justify-center">
          <EmptyState
            description="SQL response will help you define Features that can be utilised by Decision Flows"
            headline="Run Query to see SQL response"
            icon={faPlay}
          />
        </div>
      )}
      {featurePreview.error && (
        <EmptyState
          description={errorDetail}
          headline={errorTitle}
          icon={faWarning}
          variant="error"
        />
      )}
      {!featurePreview.error && (previewData || featurePreview.isPending) && (
        <>
          {!featurePreview.isPending && isLimited && (
            <div className="pr-5 pb-5" data-loc="sql-preview-limit-info">
              <Callout variant="info">
                The preview shows the first {FEATURE_QUERY_PREVIEW_LIMIT} rows.
                To get a consistent sample between runs, add an ORDER BY clause
                to the Query.
              </Callout>
            </div>
          )}
          <SQLPreviewTable
            columnTypes={columnTypes}
            dataLoc={
              featurePreview.isPending
                ? "sql-preview-table-loading"
                : "sql-preview-table"
            }
            isEditMode={isEditMode}
            isLoading={featurePreview.isPending}
            rows={previewData?.data}
            schemaColumns={schemaColumns}
            onColumnTypeChange={onColumnTypeChange}
          />

          {!featurePreview.isPending &&
            hasNoRows &&
            schemaColumns.length > 0 && (
              <div className="flex flex-1 justify-center">
                <EmptyState
                  description="To preview results, edit the Query or adjust the End date and time so the time window includes sample data."
                  headline="No data exists for this Feature Query"
                  icon={faBox}
                />
              </div>
            )}
        </>
      )}
    </div>
  );
};
